import React, { useEffect, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { messages } from '../messages';
import { PageH3Header } from './styledComponents';
import { styled } from 'styled-components';
import { Dimensions } from '../../../components/visuals';
import { useDispatch, useSelector } from 'react-redux';
import {
  fetchPartnerEmailTemplateConfig,
  updatePartnerEmailTemplateConfigAction,
} from '../duck/actions/contact';
import { selectPartnerEmailConfig, selectUpdatePartnerEmailConfig } from '../duck/selectors';
import { State } from '../../../model/State';
import EmailConfigurationRecipients from './EmailConfigurationRecipients';
import { RecipientType } from '../duck/models';
import LoadingButton from '@atlaskit/button/loading-button';
import Spinner from '@atlaskit/spinner';
import SectionMessage from '@atlaskit/section-message';
import t from '../../i18n/intl';

const TableRow = styled.tr`
  border-bottom: ${Dimensions.units(0.25)}px solid #dfe1e6;
  border-top: ${Dimensions.units(0.25)}px solid #dfe1e6;
`;

const TableBody = styled.td`
  padding: ${Dimensions.units(1)}px;
`;

const TableHeader = styled.th`
  padding: ${Dimensions.units(1)}px;
`;

const ButtonWrapper = styled.div`
  display: flex;
  justify-content: flex-end;
  margin: ${Dimensions.units(1.5)}px;
`;

const SpinnerWrapper = styled.div`
  display: flex;
  justify-content: center;
  margin-top: ${Dimensions.units(20)}px;
  height: ${Dimensions.units(20)}px;
`;

const LoadingState = () => (
  <>
    <SpinnerWrapper>
      <Spinner size="large" />
    </SpinnerWrapper>
  </>
);

const HeaderColumn = ({ messagesData }) => {
  return (
    <TableHeader scope="col">
      <FormattedMessage {...messagesData} />
    </TableHeader>
  );
};

const TimeFrameMap = {
  atlassian_invoice: messages.paymentProcessed,
  license: messages.paymentProcessed,
  reviewRequired: messages.reviewRequired,
  'kitty-partner': messages.kittyPartnerTimeFrame,
  'kitty-partner-customer-notification': messages.kittyCustomerNotificationTimeFrame,
  kitty_post_15: messages.postPurchaseTwoWeeks,
  kitty_post_212: messages.postPurchaseSevenMonths,
};

const getTimeFrame = (config: any) => {
  const timeFrame = TimeFrameMap[config?.templateName];
  if (!timeFrame) return null;
  return (
    <FormattedMessage
      {...timeFrame}
      values={{ daysCount: config.templateName.replace('kitty-partner-', '') }}
    />
  );
};

const EmailMap = {
  atlassian_invoice: messages.invoiceWithPdf,
  license: messages.licenseDetails,
  reviewRequired: messages.pendingReview,
  'kitty-partner': messages.customerReminderForExpiring,
  'kitty-partner-customer-notification': messages.reminderForExpiring,
  kitty_post_15: messages.helpfulResources,
  kitty_post_212: messages.confirmationCustomerDetails,
};

const getEmail = (config: any) => {
  const email = EmailMap[config?.templateName];
  if (!email) return null;
  return <FormattedMessage {...email} />;
};

interface RecipientTypeCheckMap {
  [key: string]: RecipientType;
}

type EmailConfigurationPanelProps = {
  partnerId: any;
};

const EmailConfigurationSection = ({ partnerId }: EmailConfigurationPanelProps) => {
  const dispatch = useDispatch();

  const emailConfig = useSelector((state) => selectPartnerEmailConfig(state as State, partnerId));
  const updateEmailConfigResponse = useSelector(selectUpdatePartnerEmailConfig);
  const [changeMapOfTypes, setChangeMapOfTypes] = useState<RecipientTypeCheckMap>({});
  const [changeMapOfEmails, setChangeMapOfEmails] = useState({});
  const [showError, setShowError] = useState<boolean>(false);

  const radioChangeHandle = (e) => {
    setChangeMapOfTypes({
      ...changeMapOfTypes,
      [e.currentTarget.name]: e.currentTarget.value,
    });
  };

  const emailChangeHandle = (e) => {
    setChangeMapOfEmails({
      ...changeMapOfEmails,
      [e.currentTarget.name]: e.currentTarget.value.trim(),
    });
    setChangeMapOfTypes({
      ...changeMapOfTypes,
      [e.currentTarget.name]: RecipientType.OTHER,
    }); // automatically select option if user put email
  };

  const saveConfiguration = () => {
    Object.entries(changeMapOfTypes).forEach(([templateName, option]) => {
      dispatch(
        updatePartnerEmailTemplateConfigAction(partnerId, {
          templateName,
          recipientType:
            option === RecipientType.OTHER && !changeMapOfEmails[templateName]
              ? RecipientType.CUSTOMER
              : option,
          recipientEmail: option === RecipientType.OTHER ? changeMapOfEmails[templateName] : null,
          configurable: null,
        }),
      );
    });
  };

  useEffect(() => {
    dispatch(fetchPartnerEmailTemplateConfig(partnerId));
  }, [partnerId]);

  useEffect(() => {
    if (updateEmailConfigResponse.success) {
      dispatch(fetchPartnerEmailTemplateConfig(partnerId));
      setChangeMapOfTypes({});
      setChangeMapOfEmails({});
      setShowError(false);
    } else if (updateEmailConfigResponse.failed) {
      setShowError(true);
    }
  }, [updateEmailConfigResponse]);

  return (
    <>
      <div>
        <PageH3Header>
          <FormattedMessage {...messages.emailConfigurationTab} />
        </PageH3Header>
        {showError && (
          <SectionMessage
            appearance="error"
            title={t('mac.partners.email.configuration.server.error')}
          >
            <FormattedMessage {...messages.emailConfigError} />
          </SectionMessage>
        )}
        <p>
          <FormattedMessage {...messages.emailConfigTabDescription} />
        </p>

        {emailConfig ? (
          <>
            <table data-cy="contacts-table">
              <thead>
                <TableRow>
                  <HeaderColumn messagesData={messages.emailTableTimeframe} />
                  <HeaderColumn messagesData={messages.emailTableEmail} />
                  <HeaderColumn messagesData={messages.emailTableRecipients} />
                </TableRow>
              </thead>

              <tbody>
                {emailConfig?.map((config) => (
                  <TableRow key={config.templateName} data-cy={`config-row=${config.templateName}`}>
                    <TableBody scope="col">{getTimeFrame(config)}</TableBody>
                    <TableBody scope="col">{getEmail(config)}</TableBody>
                    <TableBody scope="col">
                      <EmailConfigurationRecipients
                        config={config}
                        changeMapOfTypes={changeMapOfTypes}
                        radioChangeHandle={radioChangeHandle}
                        emailChangeHandle={emailChangeHandle}
                        changeMapOfEmails={changeMapOfEmails}
                      />
                    </TableBody>
                  </TableRow>
                ))}
              </tbody>
            </table>
            <ButtonWrapper>
              <LoadingButton
                isDisabled={
                  Object.keys(changeMapOfTypes).length === 0 &&
                  Object.keys(changeMapOfEmails).length === 0
                }
                data-cy="save-config-btn"
                onClick={saveConfiguration}
                isLoading={updateEmailConfigResponse.inProgress}
                appearance="primary"
              >
                <FormattedMessage {...messages.emailConfigSaveBtn} />
              </LoadingButton>
            </ButtonWrapper>
          </>
        ) : (
          <LoadingState />
        )}
      </div>
    </>
  );
};
export default EmailConfigurationSection;
